import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Loader2, Wallet, PiggyBank } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGGRESSION_LEVELS = [
  { value: "conservative", label: "Conservative", color: "text-blue-400" },
  { value: "moderate", label: "Moderate", color: "text-green-400" },
  { value: "moderate_aggressive", label: "Mod. Aggressive", color: "text-amber-400" },
  { value: "aggressive", label: "Aggressive", color: "text-red-400" },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value || 0);

export const AIInvestPlanDialog = ({ portfolio, isOpen, onClose }) => {
  const [mode, setMode] = useState("fresh");
  const [cashAmount, setCashAmount] = useState("");
  const [aggression, setAggression] = useState("moderate");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const handleClose = () => {
    setPlan(null);
    setCashAmount("");
    setMode("fresh");
    onClose();
  };

  const generatePlan = async () => {
    if (mode === "cash" && (!cashAmount || parseFloat(cashAmount) <= 0)) {
      toast.error("Enter a valid cash amount to invest");
      return;
    }
    setLoading(true);
    setPlan(null);
    try {
      const body = { aggression };
      if (mode === "cash") body.cash_amount = parseFloat(cashAmount);
      const response = await axios.post(`${API}/hl-portfolios/${portfolio.id}/ai-invest-plan`, body);
      setPlan(response.data);
      toast.success("AI investment plan generated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate AI plan");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-invest-plan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI Invest Plan
          </DialogTitle>
          <DialogDescription>
            {portfolio?.name} • {formatCurrency(portfolio?.total_value || 0)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 border-b">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={mode === "fresh" ? "default" : "outline"}
              className={`flex flex-col h-auto py-3 ${mode === "fresh" ? "ring-2 ring-purple-500" : ""}`}
              onClick={() => setMode("fresh")}
              data-testid="ai-invest-mode-fresh"
            >
              <Sparkles className="w-4 h-4 mb-1" />
              <span className="text-sm font-medium">Fresh Allocation</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">
                Plan the full {formatCurrency(portfolio?.total_value || 0)} from scratch
              </span>
            </Button>
            <Button
              variant={mode === "cash" ? "default" : "outline"}
              className={`flex flex-col h-auto py-3 ${mode === "cash" ? "ring-2 ring-purple-500" : ""}`}
              onClick={() => setMode("cash")}
              data-testid="ai-invest-mode-cash"
            >
              <PiggyBank className="w-4 h-4 mb-1" />
              <span className="text-sm font-medium">Invest New Cash</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Get picks for cash you're adding</span>
            </Button>
          </div>

          {mode === "cash" && (
            <div className="space-y-1">
              <Label htmlFor="cash-amount">Cash amount to invest (£)</Label>
              <Input
                id="cash-amount"
                type="number"
                min="1"
                placeholder="e.g. 1000"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                data-testid="ai-invest-cash-amount-input"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Risk Appetite
            </Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {AGGRESSION_LEVELS.map((level) => (
                <Button
                  key={level.value}
                  variant={aggression === level.value ? "default" : "outline"}
                  size="sm"
                  className={aggression === level.value ? "ring-2 ring-purple-500" : level.color}
                  onClick={() => setAggression(level.value)}
                  data-testid={`ai-invest-aggression-${level.value}`}
                >
                  {level.label}
                </Button>
              ))}
            </div>
          </div>

          <Button
            onClick={generatePlan}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
            data-testid="ai-invest-generate-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Plan...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate AI Plan
              </>
            )}
          </Button>
        </div>

        {plan && (
          <div className="space-y-4 py-4" data-testid="ai-invest-plan-results">
            <div className="p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-lg border border-purple-500/20">
              <p className="text-sm">{plan.strategy_summary}</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-muted/30 rounded-md text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Est. Return</p>
                <p className="text-lg font-bold text-success">{plan.expected_annual_return_pct}%</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-md text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Volatility</p>
                <p className="text-lg font-bold">{plan.expected_volatility_pct}%</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-md text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Risk Level</p>
                <Badge className="text-sm">{plan.risk_level}</Badge>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Projected Diversification Score</span>
                <span>{plan.projected_diversification_score}/100 ({plan.projected_diversification_rating})</span>
              </div>
              <Progress value={plan.projected_diversification_score} className="h-1.5" />
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Recommended Allocation of {formatCurrency(plan.amount)}
              </p>
              {plan.recommended_allocation.map((pick, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-md bg-muted/20"
                  data-testid={`ai-invest-pick-${idx}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{pick.name}</p>
                      {pick.symbol && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">{pick.symbol}</Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] px-1 py-0">{pick.type}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{pick.rationale}</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-sm font-bold tabular-nums">{pick.allocation_pct}%</p>
                    <p className="text-xs text-muted-foreground tabular-nums">{formatCurrency(pick.allocation_amount)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
